import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { SESSION_EXPIRED_ERROR } from '@/assets/AppConstants';
import { useAppContext } from '@/contexts/app';
import { getApiErrorStatus } from '@/utils/apiError';

/**
 * Bridges React Query errors into the app-level session handling.
 *
 * Legacy sections pass raw API errors to `handleAlert(error)`, where the app
 * provider turns a 401 into `requestStatus` and the Authenticated layout
 * redirects to /login. Features built on React Query (cash pooling, debts, ...)
 * handle errors locally, so without this bridge an expired session only renders
 * an inline error state.
 */
const SessionErrorForwarder = () => {
  const queryClient = useQueryClient();
  const { handleAlert, requestStatus } = useAppContext();

  const forwardedRef = useRef(false);

  useEffect(() => {
    if (requestStatus !== SESSION_EXPIRED_ERROR) {
      forwardedRef.current = false;
    }
  }, [requestStatus]);

  useEffect(() => {
    const forwardSessionError = (error: unknown) => {
      if (getApiErrorStatus(error) !== SESSION_EXPIRED_ERROR) return;
      if (forwardedRef.current) return;
      forwardedRef.current = true;
      handleAlert(error, 'error');
    };

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        forwardSessionError(event.action.error);
      }
    });

    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        forwardSessionError(event.action.error);
      }
    });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient, handleAlert]);

  return null;
};

export default SessionErrorForwarder;
